import * as THREE from 'three';
import type { Point3 } from '../game/model';

const TRAIL_POINTS = 112;

const vertexShader = `
  uniform float uTime;
  uniform float uEnergy;
  uniform float uCombo;
  uniform float uMovement;
  attribute float aEdge;
  attribute float aLife;
  varying float vEdge;
  varying float vLife;
  varying float vPulse;
  void main() {
    vec3 p = position;
    float weave = sin(aLife * 36.0 - uTime * (2.2 + uMovement * .3)) * (.018 + uCombo * .0025);
    p.y += weave * aEdge;
    vEdge = aEdge;
    vLife = aLife;
    vPulse = .7 + .3 * sin(uTime * 2.0 - aLife * 12.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;

const fragmentShader = `
  uniform vec3 uColor;
  uniform float uEnergy;
  uniform float uCombo;
  varying float vEdge;
  varying float vLife;
  varying float vPulse;
  void main() {
    float edge = 1.0 - smoothstep(.45, 1.0, abs(vEdge));
    float taper = smoothstep(0.0, .12, vLife) * (1.0 - smoothstep(.78, 1.0, vLife));
    float filament = .62 + .38 * cos(vEdge * 10.0 + vLife * 28.0);
    float alpha = edge * taper * filament * (.35 + uEnergy * .48 + min(uCombo, 10.0) * .018) * vPulse;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

export class LoomCraft {
  public readonly group = new THREE.Group();
  public readonly collisionPosition = new THREE.Vector3();
  private readonly craft = new THREE.Group();
  private readonly trail: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  private readonly history: THREE.Vector2[] = [];
  private readonly positions = new Float32Array(TRAIL_POINTS * 2 * 3);
  private readonly anchors: Point3[] = [];

  public constructor() {
    this.group.name = 'loom-craft-and-thread';
    this.trail = this.buildTrail(); this.group.add(this.trail);
    this.buildCraft(); this.group.add(this.craft);
    for (let index = 0; index < TRAIL_POINTS; index += 1) this.history.push(new THREE.Vector2());
  }

  public update(position: THREE.Vector2, elapsed: number, energy: number, combo: number, movement: number, bank: number): void {
    this.collisionPosition.set(position.x, position.y, 0);
    this.craft.position.set(position.x, position.y, 0);
    this.craft.rotation.z += (bank - this.craft.rotation.z) * .14;
    this.craft.rotation.y = Math.sin(elapsed * 2.2) * .035;
    const head = this.history[0]; if (head) head.lerp(position, .6);
    for (let index = 1; index < this.history.length; index += 1) { const point = this.history[index]; const previous = this.history[index - 1]; if (point && previous) point.lerp(previous, .19); }
    this.writeTrail();
    this.trail.material.uniforms.uTime!.value = elapsed; this.trail.material.uniforms.uEnergy!.value = energy / 100; this.trail.material.uniforms.uCombo!.value = combo; this.trail.material.uniforms.uMovement!.value = movement;
  }

  public collectAnchor(point: Point3): void { this.anchors.push({ ...point }); }
  public get collectedAnchors(): readonly Point3[] { return this.anchors; }
  public setColor(color: number): void { (this.trail.material.uniforms.uColor!.value as THREE.Color).setHex(color); }
  public reset(): void { for (const point of this.history) point.set(0, 0); this.anchors.length = 0; this.craft.position.set(0, 0, 0); }
  public dispose(): void { this.group.removeFromParent(); }

  private buildCraft(): void {
    const body = new THREE.Mesh(new THREE.ConeGeometry(.19, 1.25, 5), new THREE.MeshStandardMaterial({ color: 0xece1cf, roughness: .35, metalness: .62, emissive: 0x251710, emissiveIntensity: .4 }));
    body.rotation.x = -Math.PI / 2; body.position.z = -.12; this.craft.add(body);
    const wingPoints = [new THREE.Vector3(-.95,0,.28),new THREE.Vector3(0,0,-.2),new THREE.Vector3(.95,0,.28)];
    const wings = new THREE.Line(new THREE.BufferGeometry().setFromPoints(wingPoints), new THREE.LineBasicMaterial({ color: 0xca8b67, transparent: true, opacity: .9 })); this.craft.add(wings);
    const eye = new THREE.Mesh(new THREE.OctahedronGeometry(.085,0), new THREE.MeshBasicMaterial({ color: 0xf7e5ce })); eye.position.z = -.7; this.craft.add(eye);
  }

  private buildTrail(): THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> {
    const indices: number[] = []; const edges = new Float32Array(TRAIL_POINTS * 2); const life = new Float32Array(TRAIL_POINTS * 2);
    for (let index = 0; index < TRAIL_POINTS; index += 1) { const offset = index * 2; edges[offset] = -1; edges[offset + 1] = 1; life[offset] = index / (TRAIL_POINTS - 1); life[offset + 1] = life[offset] ?? 0; if (index < TRAIL_POINTS - 1) indices.push(offset, offset + 2, offset + 1, offset + 1, offset + 2, offset + 3); }
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3)); geometry.setAttribute('aEdge', new THREE.BufferAttribute(edges,1)); geometry.setAttribute('aLife',new THREE.BufferAttribute(life,1)); geometry.setIndex(indices);
    const material = new THREE.ShaderMaterial({ vertexShader, fragmentShader, uniforms: { uTime:{value:0},uEnergy:{value:1},uCombo:{value:0},uMovement:{value:0},uColor:{value:new THREE.Color(0xca8b67)} }, transparent:true, depthWrite:false, side:THREE.DoubleSide, blending:THREE.AdditiveBlending });
    return new THREE.Mesh(geometry, material);
  }

  private writeTrail(): void {
    for (let index = 0; index < TRAIL_POINTS; index += 1) { const point = this.history[index]; if (!point) continue; const width = .09 * (1 - index / TRAIL_POINTS) + .008; const z = index * .075; const offset = index * 6; this.positions[offset] = point.x - width; this.positions[offset+1] = point.y; this.positions[offset+2] = z; this.positions[offset+3] = point.x + width; this.positions[offset+4] = point.y; this.positions[offset+5] = z; }
    const position = this.trail.geometry.getAttribute('position'); position.needsUpdate = true; this.trail.geometry.computeBoundingSphere();
  }
}
